(function () {
  'use strict';

  const SELECTOR = '[data-au-home-highlights]';
  const READY = 'auHighlightsReady';

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function clampIndex(index, length) {
    if (!length) return 0;
    if (index < 0) return length - 1;
    if (index >= length) return 0;
    return index;
  }

  function getClosestIndex(track, cards) {
    if (!track || !cards.length) return 0;

    const trackLeft = track.getBoundingClientRect().left;
    let bestIndex = 0;
    let bestDistance = Infinity;

    cards.forEach((card, index) => {
      const distance = Math.abs(card.getBoundingClientRect().left - trackLeft);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    });

    return bestIndex;
  }

  function formatCountdown(distance) {
    const totalSeconds = Math.max(Math.floor(distance / 1000), 0);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    if (days > 0) return `${days}d ${hours}h left`;
    if (hours > 0) return `${hours}h ${minutes}m left`;
    return `${minutes}m left`;
  }

  function initScheduledCards(section) {
    // A campaign with a future start date stays hidden until it begins.
    // Fail-safe: a blank or unparseable date shows the card.
    const scheduled = [];

    Array.from(section.querySelectorAll('[data-au-highlights-card][data-au-campaign-start]')).forEach((card) => {
      const startTime = Date.parse(card.dataset.auCampaignStart || '');
      if (!Number.isFinite(startTime) || startTime <= Date.now()) return;

      card.setAttribute('hidden', '');
      card.setAttribute('data-au-campaign-scheduled', 'true');

      const dot = section.querySelector(
        '[data-au-highlights-dot="' + (card.dataset.auHighlightsIndex || '') + '"]'
      );
      if (dot) dot.setAttribute('hidden', '');

      scheduled.push({ card: card, dot: dot, startTime: startTime });
    });

    if (!scheduled.length) return;

    /*
      WAKE A SCHEDULED CARD WHEN ITS START TIME PASSES.

      This sweep runs once at load, so a card whose start_at fell during the
      session stayed hidden until a reload — the mirror image of the expiry bug
      fixed in initCountdowns(). A drop that goes live at 18:00 was invisible to
      anyone who had the homepage open beforehand, which is exactly the audience
      a scheduled reveal is aimed at.

      Reveals the card AND its dot together, matching how the expiry sweep hides
      them, so the pagination can never disagree with the visible cards.

      A 30s tick is deliberate: campaign starts are minute-precision at best, and
      a 1s timer on the homepage is a wasteful wake-up for no perceptible gain.
      The interval clears itself once the last scheduled card has appeared, and
      is registered for teardown so a long Theme Editor session cannot stack
      several of them.
    */
    let pending = scheduled.slice();

    const wakeTick = window.setInterval(() => {
      const now = Date.now();
      pending = pending.filter((entry) => {
        if (entry.startTime > now) return true;

        entry.card.removeAttribute('hidden');
        entry.card.removeAttribute('data-au-campaign-scheduled');
        if (entry.dot) entry.dot.removeAttribute('hidden');
        return false;
      });

      if (!pending.length) {
        window.clearInterval(wakeTick);
        section.auHighlightsWakeTimer = null;
      }
    }, 30000);

    section.auHighlightsWakeTimer = wakeTick;
  }

  function initCountdowns(section) {
    const countdowns = Array.from(section.querySelectorAll('[data-au-highlights-countdown]'));
    if (!countdowns.length) return;

    // Load-time sweep: an offer whose countdown has already ended should not
    // greet visitors with "Offer ended" — hide the slide and its dot entirely.
    countdowns.forEach((node) => {
      const rawEnd = node.dataset.end;
      const endTime = rawEnd ? Date.parse(rawEnd) : NaN;

      if (!Number.isFinite(endTime) || endTime - Date.now() > 0) return;

      const card = node.closest('[data-au-highlights-card]');
      if (!card || !card.classList.contains('au-home-highlight-card--intent-offer')) return;

      card.setAttribute('hidden', '');
      card.setAttribute('data-au-campaign-expired', 'true');

      const dot = section.querySelector(
        '[data-au-highlights-dot="' + (card.dataset.auHighlightsIndex || '') + '"]'
      );
      if (dot) dot.setAttribute('hidden', '');
    });

    let intervalId = null;

    function tick() {
      if (intervalId && !countdowns.some((node) => node.isConnected)) {
        window.clearInterval(intervalId);
        intervalId = null;
        return;
      }

      const now = Date.now();

      countdowns.forEach((node) => {
        if (!node.isConnected) return;

        const rawEnd = node.dataset.end;
        const endTime = rawEnd ? Date.parse(rawEnd) : NaN;

        if (!Number.isFinite(endTime)) {
          node.hidden = true;
          return;
        }

        const distance = endTime - now;

        if (distance <= 0) {
          node.textContent = node.dataset.expiredText || 'Offer ended';
          node.classList.add('is-expired');

          /*
            EXPIRY MID-SESSION MUST MATCH EXPIRY AT LOAD.

            The load-time sweep above hides an expired offer card AND its dot.
            This tick did not, so the same offer had three different states:
              - already expired at load    -> card hidden
              - expires while page is open -> card VISIBLE, reading "Offer ended"
              - reload after that          -> card hidden again
            Meanwhile Shopify may still be applying the discount, so "Offer
            ended" can be wrong as well as inconsistent.

            Hiding here converges the two paths. Guarded on the same
            intent-offer class the load sweep uses, so a non-offer card with a
            countdown (e.g. the "Launching" pre-order slide) still just shows
            its expired label rather than vanishing.
          */
          const expiredCard = node.closest('[data-au-highlights-card]');
          if (
            expiredCard &&
            !expiredCard.hasAttribute('hidden') &&
            expiredCard.classList.contains('au-home-highlight-card--intent-offer')
          ) {
            expiredCard.setAttribute('hidden', '');
            expiredCard.setAttribute('data-au-campaign-expired', 'true');

            const expiredDot = section.querySelector(
              '[data-au-highlights-dot="' + (expiredCard.dataset.auHighlightsIndex || '') + '"]'
            );
            if (expiredDot) expiredDot.setAttribute('hidden', '');
          }
          return;
        }

        const label = node.textContent && !node.dataset.originalLabel
          ? node.textContent.trim()
          : (node.dataset.originalLabel || 'Ends in');

        node.dataset.originalLabel = label;
        node.textContent = `${label} · ${formatCountdown(distance)}`;
        node.classList.remove('is-expired');
      });
    }

    tick();
    intervalId = window.setInterval(tick, 60000);
  }

  function offerPropertiesFromProductItem(item) {
    const properties = {};
    if (!item || !item.dataset) return properties;

    if (item.dataset.auOfferGroup) {
      properties._aurealm_offer_group = item.dataset.auOfferGroup;
    }
    if (item.dataset.auB2g1Excluded === 'true') {
      properties._aurealm_b2g1_excluded = 'true';
    }
    if (item.dataset.auGiftProgressExcluded === 'true') {
      properties._aurealm_gift_progress_excluded = 'true';
    }

    return properties;
  }

  function initProductCards(rail) {
    const items = Array.from(rail.querySelectorAll('[data-au-highlights-product-item]'));

    items.forEach((item) => {
      if (item.dataset.auHighlightsProductReady === 'true') return;
      item.dataset.auHighlightsProductReady = 'true';

      const select = item.querySelector('[data-au-highlights-product-variant]');
      const button = item.querySelector('[data-au-highlights-product-add]');
      const label = item.querySelector('[data-au-highlights-product-add-label]');
      const status = item.querySelector('[data-au-highlights-product-status]');
      const price = item.querySelector('[data-au-highlights-product-price]');
      const compare = item.querySelector('[data-au-highlights-product-compare]');
      const badge = item.querySelector('[data-au-highlights-product-badge]');

      if (!button || !label) return;

      const defaultText = 'Add to Cart';
      let resetTimer = null;

      function announce(message) {
        if (status) status.textContent = message || '';
      }

      function setButtonState(state, message) {
        button.classList.toggle('is-loading', state === 'loading');
        button.classList.toggle('is-added', state === 'added');
        button.classList.toggle('is-error', state === 'error');
        button.toggleAttribute('aria-busy', state === 'loading');
        label.textContent = message || defaultText;
      }

      function updateVariant() {
        if (!select) return;

        const option = select.options[select.selectedIndex];
        if (!option) return;

        const discount = Number(option.dataset.discountPercent || 0);
        const comparePrice = option.dataset.comparePrice || '';

        button.dataset.variantId = option.value || '';
        button.disabled = option.disabled || !option.value;
        button.setAttribute(
          'aria-label',
          button.disabled ? 'Selected option is sold out' : 'Add selected option to cart'
        );

        if (price && option.dataset.price) price.textContent = option.dataset.price;

        if (compare) {
          compare.textContent = comparePrice;
          compare.hidden = !comparePrice;
        }

        if (badge) {
          badge.textContent = discount > 0 ? `${discount}% OFF` : '';
          badge.hidden = discount <= 0;
        }

        setButtonState('idle', button.disabled ? 'Sold Out' : defaultText);
        announce('');
      }

      if (select) {
        select.addEventListener('change', updateVariant);
        updateVariant();
      }

      button.addEventListener('click', () => {
        if (button.disabled || button.classList.contains('is-loading')) return;

        const variantId = button.dataset.variantId || (select && select.value) || '';
        if (!variantId) {
          setButtonState('error', 'Choose Option');
          announce('Choose an available option before adding this product.');
          return;
        }

        if (!window.AurealmCart || typeof window.AurealmCart.addId !== 'function') {
          window.AUREALM_DEBUG && console.warn('[Aurealm Highlights] cart-core missing — falling back to /cart.');
          window.location.href = '/cart';
          return;
        }

        if (resetTimer) window.clearTimeout(resetTimer);
        setButtonState('loading', 'Adding…');
        announce('Adding product to your cart.');

        window.AurealmCart
          .addId(variantId, 1, offerPropertiesFromProductItem(item))
          .then(() => {
            setButtonState('added', 'Added');
            announce('Added to your cart.');
            resetTimer = window.setTimeout(() => {
              setButtonState('idle', defaultText);
            }, 1400);
          })
          .catch((error) => {
            window.AUREALM_DEBUG && console.error('[Aurealm Highlights] Cart error:', error);
            setButtonState('error', 'Try Again');
            announce('We could not add this product. Please try again.');
            resetTimer = window.setTimeout(() => {
              setButtonState('idle', defaultText);
            }, 1800);
          });
      });
    });
  }

  function initProductRail(section) {
    const rail = section.querySelector('[data-au-highlights-products]');
    const track = section.querySelector('[data-au-highlights-product-track]');
    const prev = section.querySelector('[data-au-highlights-products-prev]');
    const next = section.querySelector('[data-au-highlights-products-next]');

    if (!rail || !track) return;

    initProductCards(rail);

    function scrollProductRail(direction) {
      const amount = Math.max(Math.round(track.clientWidth * 0.82), 260);

      track.scrollBy({
        left: amount * direction,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
    }

    function updateArrowState() {
      const maxScroll = track.scrollWidth - track.clientWidth;
      const current = track.scrollLeft;

      if (prev) prev.disabled = current <= 4;
      if (next) next.disabled = current >= maxScroll - 4;
    }

    if (prev) {
      prev.addEventListener('click', () => {
        scrollProductRail(-1);
      });
    }

    if (next) {
      next.addEventListener('click', () => {
        scrollProductRail(1);
      });
    }

    track.addEventListener('scroll', () => {
      window.requestAnimationFrame(updateArrowState);
    }, { passive: true });

    window.addEventListener('resize', updateArrowState);
    updateArrowState();
  }

  function initMainCarousel(section) {
    const track = section.querySelector('[data-au-highlights-track]');
    const cards = Array.from(section.querySelectorAll('[data-au-highlights-card]:not([hidden])'));
    const prev = section.querySelector('[data-au-highlights-prev]');
    const next = section.querySelector('[data-au-highlights-next]');
    const dots = Array.from(section.querySelectorAll('[data-au-highlights-dot]:not([hidden])'));
    const dotsContainer = section.querySelector('[data-au-highlights-dots]');

    if (!track || !cards.length) return;

    let activeIndex = 0;
    let autoplayTimer = null;
    let scrollTimer = null;
    let userPaused = false;
    let hasOverflow = true;

    const autoplayEnabled = section.dataset.autoplay === 'true' && cards.length > 1 && !prefersReducedMotion();
    const autoplaySpeed = Math.max(Number(section.dataset.autoplaySpeed) || 6000, 4000);

    function updateUI(index) {
      activeIndex = clampIndex(index, cards.length);

      dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === activeIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    /* Last index the track can actually make the leftmost card. With several
       cards visible per viewport, indexes past this all clamp to the same max
       scroll position — navigating to them desyncs the dots from the view. */
    function maxReachableIndex() {
      const maxLeft = track.scrollWidth - track.clientWidth;
      for (let i = 0; i < cards.length; i++) {
        if (cards[i].offsetLeft >= maxLeft - 4) return i;
      }
      return cards.length - 1;
    }

    /* Hide dots that cannot be reached at the current width.

       goTo() already clamps an unreachable target to the end position, and
       that clamping is correct — aria-current then reports where the track
       ACTUALLY is rather than where the dot claimed to go. The defect is one
       step earlier: with 7 cards at 292px in a 1232px track only 4 positions
       are reachable on desktop, yet all 7 dots render. Dots 5-7 therefore look
       broken — clicking any of them lands on the same end position, because
       there is nothing further to scroll to.

       Rendering only the reachable dots removes the dead controls instead of
       pretending they navigate. Re-run on resize: at mobile widths the track
       is 1-up, every index is reachable, and all 7 come back.

       Dots already hidden for another reason (an expired campaign card) are
       left alone — they are excluded from `dots` at init and never restored
       here. */
    function syncDotVisibility() {
      if (!dots.length) return;

      const limit = hasOverflow ? maxReachableIndex() : 0;

      dots.forEach((dot, dotIndex) => {
        const unreachable = dotIndex > limit;
        dot.hidden = unreachable;
        // hidden alone leaves it in the tab order in some engines.
        if (unreachable) {
          dot.setAttribute('tabindex', '-1');
          dot.setAttribute('aria-hidden', 'true');
        } else {
          dot.removeAttribute('tabindex');
          dot.removeAttribute('aria-hidden');
        }
      });
    }

    function goTo(index, behavior) {
      const limit = maxReachableIndex();
      let targetIndex = clampIndex(index, cards.length);

      if (targetIndex > limit) {
        // Stepping forward from the end position wraps to the start;
        // any other unreachable target (e.g. a tail dot) lands on the
        // end position so the active dot always matches the view.
        targetIndex = activeIndex >= limit && index === activeIndex + 1 ? 0 : limit;
      }

      const targetCard = cards[targetIndex];

      if (!targetCard) return;

      track.scrollTo({
        left: targetCard.offsetLeft,
        behavior: behavior || (prefersReducedMotion() ? 'auto' : 'smooth')
      });

      updateUI(targetIndex);
    }

    function stopAutoplay() {
      if (autoplayTimer) {
        window.clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    }

    function startAutoplay() {
      if (!autoplayEnabled || autoplayTimer || userPaused || !hasOverflow) return;

      autoplayTimer = window.setInterval(() => {
        goTo(activeIndex + 1);
      }, autoplaySpeed);
    }

    function updateOverflowState() {
      const nextHasOverflow = track.scrollWidth - track.clientWidth > 4;

      if (nextHasOverflow === hasOverflow) {
        if (nextHasOverflow) {
          // Same overflow state, but the width may still have changed enough
          // to change how many positions are reachable.
          syncDotVisibility();
          updateUI(getClosestIndex(track, cards));
        }
        return;
      }

      hasOverflow = nextHasOverflow;
      section.classList.toggle('au-home-highlights--no-overflow', !hasOverflow);

      if (prev) prev.disabled = !hasOverflow;
      if (next) next.disabled = !hasOverflow;
      if (dotsContainer) dotsContainer.setAttribute('aria-hidden', hasOverflow ? 'false' : 'true');
      updatePauseUI();

      if (!hasOverflow) {
        stopAutoplay();
        syncDotVisibility();
        updateUI(0);

        if (Math.abs(track.scrollLeft) > 4) {
          track.scrollTo({ left: 0, behavior: 'auto' });
        }

        return;
      }

      syncDotVisibility();
      updateUI(getClosestIndex(track, cards));
      startAutoplay();
    }

    const pauseBtn = section.querySelector('[data-au-highlights-pause]');

    function updatePauseUI() {
      if (!pauseBtn) return;

      if (!autoplayEnabled || !hasOverflow) {
        pauseBtn.hidden = true;
        return;
      }

      pauseBtn.hidden = false;
      pauseBtn.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
      pauseBtn.setAttribute('aria-label', userPaused ? 'Play highlights autoplay' : 'Pause highlights autoplay');
      pauseBtn.classList.toggle('is-paused', userPaused);
    }

    if (pauseBtn) {
      pauseBtn.addEventListener('click', () => {
        userPaused = !userPaused;

        if (userPaused) {
          stopAutoplay();
        } else {
          startAutoplay();
        }

        updatePauseUI();
      });
    }

    if (prev) {
      prev.addEventListener('click', () => {
        userPaused = true;
        stopAutoplay();
        updatePauseUI();
        goTo(activeIndex - 1);
      });
    }

    if (next) {
      next.addEventListener('click', () => {
        userPaused = true;
        stopAutoplay();
        updatePauseUI();
        goTo(activeIndex + 1);
      });
    }

    dots.forEach((dot, dotPosition) => {
      dot.addEventListener('click', () => {
        userPaused = true;
        stopAutoplay();
        updatePauseUI();
        goTo(dotPosition);
      });
    });

    /*
      CAROUSEL KEYBOARD CONTRACT (ArrowLeft/ArrowRight/Home/End).

      The arrows and dots are native buttons, so the carousel was already
      operable with Tab + Enter — this was never "keyboard inaccessible". What
      was missing is the pattern a carousel with role="region"
      aria-roledescription="carousel" advertises: arrow keys move between
      slides, Home/End jump to the ends.

      Scoped to the section and deliberately narrow:
       - ignores the event when focus is in a field or contenteditable, so the
         waitlist email input on slide 5 keeps normal caret movement;
       - ignores it when a modifier is held, leaving browser shortcuts alone;
       - only acts when focus is actually inside this section, so it cannot
         hijack arrow keys for the rest of the page.

      Home/End use maxReachableIndex() rather than cards.length-1: past that
      index every position clamps to the same scroll offset, so "End" must
      land on the last REACHABLE slide or the active dot desyncs from the view.
    */
    section.addEventListener('keydown', (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (!hasOverflow) return;

      const el = event.target;
      if (el && el.closest && el.closest('input, textarea, select, [contenteditable="true"]')) return;
      if (!section.contains(document.activeElement)) return;

      let target = null;
      if (event.key === 'ArrowRight') target = activeIndex + 1;
      else if (event.key === 'ArrowLeft') target = activeIndex - 1;
      else if (event.key === 'Home') target = 0;
      else if (event.key === 'End') target = maxReachableIndex();
      else return;

      event.preventDefault();
      userPaused = true;
      stopAutoplay();
      updatePauseUI();
      goTo(target);
    });

    track.addEventListener('scroll', () => {
      if (!hasOverflow) return;
      if (scrollTimer) window.clearTimeout(scrollTimer);

      scrollTimer = window.setTimeout(() => {
        updateUI(getClosestIndex(track, cards));
      }, 80);
    }, { passive: true });

    section.addEventListener('mouseenter', stopAutoplay);
    section.addEventListener('mouseleave', startAutoplay);
    section.addEventListener('focusin', stopAutoplay);
    section.addEventListener('focusout', startAutoplay);

    /*
      PAUSE AUTOPLAY WHILE THE TAB IS HIDDEN.

      Hover and focus already pause, but a backgrounded tab did neither, so the
      carousel kept advancing on a timer nobody could see — burning wake-ups on
      mobile and, worse, leaving the visitor on an arbitrary slide when they
      returned rather than where they left off.

      Currently LATENT: enable_autoplay is false in live settings, so this
      changes nothing today. It is the guard that makes turning autoplay on
      safe, which is why it belongs here rather than in a to-do.

      startAutoplay() already respects userPaused, hasOverflow and reduced
      motion, so resuming on visible cannot override a deliberate pause.
    */
    const onVisibility = () => {
      if (document.hidden) stopAutoplay();
      else startAutoplay();
    };

    document.addEventListener('visibilitychange', onVisibility);
    section.auHighlightsVisibilityHandler = onVisibility;

    /*
      Named rather than inline so shopify:section:unload can remove it.

      This is registered on DOCUMENT, not on the section, so it outlives the
      section node. Every Theme Editor re-render used to add another one; the
      isConnected guard made stale copies inert but their closures still held
      `cards`, `track` and `dots`, so a long editing session accumulated whole
      carousel states in memory. Editor-only, never seen by a customer — but
      it is also the kind of leak that quietly makes the editor sluggish.
    */
    const onBlockSelect = (event) => {
      if (!section.isConnected) return;

      const block = event.target && event.target.closest
        ? event.target.closest('[data-au-highlights-card]')
        : null;

      if (!block || !section.contains(block)) return;

      stopAutoplay();

      /*
        `cards` holds only the VISIBLE cards (":not([hidden])" where it is built
        above), but data-au-highlights-index is the block's ORIGINAL position,
        written by Liquid across every block including hidden ones. The moment an
        earlier card is hidden the two stop agreeing — and one is: the first
        campaign card has countdown_end 2026-07-31 and is hidden as expired, so
        every later block is off by one. Selecting "Rakhi" in the Theme Editor
        scrolled to "A Gift from the Realm".

        Resolve the block's position within the visible set instead. A block that
        is itself hidden has no visible position, so there is nothing sensible to
        scroll to — leave the carousel where it is rather than guessing.
      */
      const visibleIndex = cards.indexOf(block);
      if (visibleIndex < 0) return;

      goTo(visibleIndex, 'auto');
    };

    document.addEventListener('shopify:block:select', onBlockSelect);
    section.auHighlightsBlockSelectHandler = onBlockSelect;

    updateUI(0);

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(updateOverflowState);
      });
      resizeObserver.observe(track);
      section.auHighlightsResizeObserver = resizeObserver;
    } else {
      window.addEventListener('resize', updateOverflowState);
      section.auHighlightsResizeHandler = updateOverflowState;
    }

    window.requestAnimationFrame(() => {
      updateOverflowState();
      startAutoplay();
      updatePauseUI();
    });
  }

  function initWaitlistForms(section) {
    const forms = Array.from(section.querySelectorAll('.au-home-highlight-card__waitlist-form'));
    if (!forms.length) return;

    const companyId = section.getAttribute('data-klaviyo-company-id') || '';
    const listId = section.getAttribute('data-klaviyo-list-id') || '';
    const successMessage =
      section.getAttribute('data-waitlist-success') ||
      'Almost there — check your inbox to confirm early access.';
    const fallbackSuccessMessage =
      section.getAttribute('data-waitlist-fallback-success') ||
      'Your place is saved. We’ll send the first reveal by email.';
    const errorMessage =
      section.getAttribute('data-waitlist-error') ||
      'We could not save your place. Please try again.';

    function isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
    }

    function subscribeToKlaviyo(email, campaign) {
      if (!companyId || !listId) {
        return Promise.resolve({ subscribed: false, reason: 'missing-config' });
      }

      const endpoint =
        'https://a.klaviyo.com/client/subscriptions/?company_id=' +
        encodeURIComponent(companyId);

      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Homepage Early Access',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  properties: {
                    'Aurealm Early Access Campaign': campaign
                  },
                  subscriptions: {
                    email: {
                      marketing: {
                        consent: 'SUBSCRIBED'
                      }
                    }
                  }
                }
              }
            }
          },
          relationships: {
            list: {
              data: { type: 'list', id: listId }
            }
          }
        }
      };

      return fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          revision: '2026-04-15'
        },
        body: JSON.stringify(payload)
      }).then((response) => {
        if (response.status === 202 || response.ok) {
          return { subscribed: true };
        }
        throw new Error('Klaviyo early-access subscribe failed: HTTP ' + response.status);
      });
    }

    function submitToShopify(form) {
      const formData = new FormData(form);

      return fetch(form.action || window.location.pathname, {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      }).then((response) => {
        if (!response.ok) {
          throw new Error('Shopify early-access submit failed: HTTP ' + response.status);
        }
        return { subscribed: true };
      });
    }

    function trackSignup(email, campaign, klaviyoSubscribed, shopifySubscribed) {
      const properties = {
        source: 'Homepage Highlights',
        campaign: campaign,
        klaviyo_subscribed: klaviyoSubscribed,
        shopify_subscribed: shopifySubscribed,
        page_url: window.location.href
      };

      if (window.AurealmKlaviyo) {
        window.AurealmKlaviyo.identify(email, {
          'Aurealm Early Access Campaign': campaign,
          'Aurealm Signup Source': 'Homepage Highlights'
        });
        window.AurealmKlaviyo.track('Aurealm Early Access Joined', properties);
        return;
      }

      window._learnq = window._learnq || [];
      window._learnq.push([
        'identify',
        {
          '$email': email,
          'Aurealm Early Access Campaign': campaign,
          'Aurealm Signup Source': 'Homepage Highlights'
        }
      ]);
      window._learnq.push(['track', 'Aurealm Early Access Joined', properties]);
    }

    forms.forEach((form) => {
      if (form.dataset.auWaitlistReady === 'true') return;
      form.dataset.auWaitlistReady = 'true';

      const card = form.closest('[data-au-highlights-card]');
      const cardSurface = form.closest('[data-au-highlights-waitlist-card]');
      const input = form.querySelector('[data-au-highlights-waitlist-email]');
      const button = form.querySelector('[data-au-highlights-waitlist-submit]');
      const status = form.querySelector('[data-au-highlights-waitlist-status]');
      const campaign =
        (cardSurface && cardSurface.getAttribute('data-waitlist-campaign')) ||
        'Upcoming Aurealm ritual';
      const defaultButtonText = button ? button.textContent.trim() : 'Join Early Access';

      if (!input || !button || !status) return;

      function setStatus(message, state) {
        status.textContent = message || '';
        status.classList.toggle('is-success', state === 'success');
        status.classList.toggle('is-error', state === 'error');
        status.setAttribute('aria-live', state === 'error' ? 'assertive' : 'polite');
      }

      function setBusy(isBusy) {
        button.disabled = isBusy;
        button.toggleAttribute('aria-busy', isBusy);
        if (isBusy) button.textContent = 'Joining…';
      }

      input.addEventListener('input', () => {
        if (input.getAttribute('aria-invalid') === 'true') {
          input.setAttribute('aria-invalid', 'false');
          setStatus('', null);
        }
      });

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        event.stopPropagation();

        const email = String(input.value || '').trim().toLowerCase();

        if (!isValidEmail(email)) {
          input.setAttribute('aria-invalid', 'true');
          setStatus('Please enter a valid email address.', 'error');
          input.focus();
          return;
        }

        input.setAttribute('aria-invalid', 'false');
        setStatus('', null);
        setBusy(true);
        if (card) card.classList.remove('is-waitlist-success');

        try {
          const results = await Promise.allSettled([
            subscribeToKlaviyo(email, campaign),
            submitToShopify(form)
          ]);

          const klaviyoSucceeded =
            results[0].status === 'fulfilled' &&
            results[0].value &&
            results[0].value.subscribed === true;
          const shopifySucceeded =
            results[1].status === 'fulfilled' &&
            results[1].value &&
            results[1].value.subscribed === true;

          if (!klaviyoSucceeded && !shopifySucceeded) {
            throw new Error('All early-access endpoints failed.');
          }

          trackSignup(email, campaign, klaviyoSucceeded, shopifySucceeded);

          form.reset();
          button.textContent = klaviyoSucceeded ? 'Check your inbox' : 'Place saved';
          button.disabled = true;
          button.removeAttribute('aria-busy');
          setStatus(
            klaviyoSucceeded ? successMessage : fallbackSuccessMessage,
            'success'
          );
          if (card) card.classList.add('is-waitlist-success');

          document.dispatchEvent(
            new CustomEvent('aurealm:early-access:joined', {
              detail: {
                email: email,
                campaign: campaign,
                listId: listId,
                klaviyoSubscribed: klaviyoSucceeded,
                shopifySubscribed: shopifySucceeded
              }
            })
          );
        } catch (error) {
          setStatus(errorMessage, 'error');
          button.textContent = defaultButtonText;
          button.disabled = false;
          button.removeAttribute('aria-busy');

          if (window.AUREALM_DEBUG && window.console && window.console.warn) {
            window.console.warn('Aurealm early-access error:', error);
          }
        }
      });
    });
  }

  function initSection(section) {
    if (!section || section.dataset[READY] === 'true') return;

    section.dataset[READY] = 'true';

    initScheduledCards(section);
    initCountdowns(section);
    initProductRail(section);
    initMainCarousel(section);
    initWaitlistForms(section);

  }

  function initAll(root) {
    const scope = root || document;
    scope.querySelectorAll(SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  /*
    THEME EDITOR TEARDOWN.

    Everything this section registers OUTSIDE its own DOM subtree has to be
    released when Shopify replaces the section, or each edit leaks another copy:

      - shopify:block:select   on document — outlives the section node entirely
      - visibilitychange       on document — same
      - ResizeObserver         holds a reference to `track`
      - resize                 on window, the no-ResizeObserver fallback
      - the scheduled-card wake interval

    Listeners bound to elements inside the section (arrows, dots, keydown,
    mouseenter/focusin, scroll) need nothing here — they are garbage-collected
    with the nodes they are attached to.

    The isConnected guards already made stale handlers inert, so this is not a
    correctness fix: it stops long editing sessions accumulating dead carousel
    closures, each still holding cards, dots and track.

    Also clears the READY flag, so if Shopify ever unloads and re-loads the same
    node, initSection() will run again instead of returning early.
  */
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target;
    if (!section || !section.querySelectorAll) return;

    const sections = section.matches && section.matches(SELECTOR)
      ? [section]
      : Array.from(section.querySelectorAll(SELECTOR));

    sections.forEach((node) => {
      if (node.auHighlightsBlockSelectHandler) {
        document.removeEventListener('shopify:block:select', node.auHighlightsBlockSelectHandler);
        node.auHighlightsBlockSelectHandler = null;
      }

      if (node.auHighlightsVisibilityHandler) {
        document.removeEventListener('visibilitychange', node.auHighlightsVisibilityHandler);
        node.auHighlightsVisibilityHandler = null;
      }

      if (node.auHighlightsResizeObserver) {
        node.auHighlightsResizeObserver.disconnect();
        node.auHighlightsResizeObserver = null;
      }

      if (node.auHighlightsResizeHandler) {
        window.removeEventListener('resize', node.auHighlightsResizeHandler);
        node.auHighlightsResizeHandler = null;
      }

      if (node.auHighlightsWakeTimer) {
        window.clearInterval(node.auHighlightsWakeTimer);
        node.auHighlightsWakeTimer = null;
      }

      delete node.dataset[READY];
    });
  });
})();
